import { Component, computed, effect, inject } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { trigger, transition, style, animate } from '@angular/animations';
import { UiStore } from '../stores/ui.store';
import { AppStore } from '../stores/app.store';
import { AuthStore } from '../stores/auth.store';
import { OrganizationManagerService } from '../managers/organization-manager.service';
import { Roles } from '../core/auth/role-constants';
import { IconComponent } from '../shared/components/icon/icon.component';

interface NavItem {
  label: string;
  route: string;
  icon: string;
  roles?: string[];
  worksWithoutFacility?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

interface DisplayNavItem extends NavItem {
  locked: boolean;
  hasWarning: boolean;
}

interface DisplayNavGroup {
  label: string;
  items: DisplayNavItem[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, IconComponent],
  templateUrl: './sidebar.component.html',
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms 100ms ease-in', style({ opacity: 1 })),
      ]),
    ]),
  ],
})
export class SidebarComponent {
  protected readonly uiStore = inject(UiStore);
  private readonly authStore = inject(AuthStore);
  private readonly appStore = inject(AppStore);
  private readonly organizationManager = inject(OrganizationManagerService);

  private readonly adminOnly = [Roles.GlobalAdmin, Roles.OrgAdmin, Roles.FacilityAdmin];
  private readonly globalAdminOnly = [Roles.GlobalAdmin];

  private readonly allNavGroups: NavGroup[] = [
    {
      label: 'Overview',
      items: [
        { label: 'Home', route: '/home', icon: 'home' },
      ],
    },
    {
      label: 'People & Pets',
      items: [
        { label: 'Employees', route: '/employees', icon: 'users', roles: this.adminOnly },
        { label: 'Owners', route: '/owners', icon: 'user-group' },
        { label: 'Pets', route: '/pets', icon: 'heart' },
      ],
    },
    {
      label: 'Operations',
      items: [
        { label: 'Checklists', route: '/checklists', icon: 'clipboard-document-list' },
        { label: 'Conditions', route: '/conditions', icon: 'tag' },
        { label: 'Forms', route: '/forms', icon: 'document-text' },
        { label: 'Pet Groups', route: '/pet-groups', icon: 'squares-2x2' },
        { label: 'Reservations', route: '/reservations', icon: 'calendar-days' },
        { label: 'Tasks', route: '/tasks', icon: 'clipboard-document-check' },
        { label: 'Timings', route: '/timings', icon: 'clock' },
      ],
    },
    {
      label: 'Configuration',
      items: [
        { label: 'Facilities', route: '/facilities', icon: 'building-storefront', roles: this.adminOnly },
        { label: 'Facility Onboarding', route: '/onboarding', icon: 'rocket-launch', roles: [Roles.GlobalAdmin, Roles.OrgAdmin], worksWithoutFacility: true },
        { label: 'Integrations', route: '/integrations', icon: 'link', roles: this.adminOnly },
        { label: 'Organizations', route: '/organizations', icon: 'building-office', roles: this.adminOnly },
        { label: 'Service Add-Ons', route: '/service-add-ons', icon: 'plus-circle' },
        { label: 'Services', route: '/services', icon: 'briefcase' },
        { label: 'Suite Groups', route: '/suite-groups', icon: 'squares-2x2' },
        { label: 'Suites', route: '/suites', icon: 'key' },
      ],
    },
    {
      label: 'Analytics',
      items: [
        { label: 'Reports', route: '/reports', icon: 'chart-bar', roles: this.adminOnly },
      ],
    },
    {
      label: 'Admin',
      items: [
        { label: 'Broadcast Banners', route: '/broadcast-banners', icon: 'megaphone', roles: this.globalAdminOnly, worksWithoutFacility: true },
        { label: 'Feedback', route: '/feedback', icon: 'chat-bubble-left', roles: this.globalAdminOnly },
        { label: 'User Management', route: '/user-management', icon: 'shield-check', roles: this.adminOnly },
      ],
    },
  ];

  /**
   * True once we know the user has no facility. Stays false while that is still loading, so the
   * nav doesn't disable itself on first paint and then light back up.
   */
  protected readonly isAwaitingFirstFacility = computed(() => this.appStore.hasAnyFacility() === false);

  private readonly canViewOrganizations = computed(() => {
    const userRoles = this.authStore.roles();
    const canViewOrganizations = this.adminOnly.some(role => userRoles.includes(role));

    return canViewOrganizations;
  });

  private readonly hasUnmappedOrganizationSchedules = computed(() =>
    this.organizationManager.scheduleMappingStatuses().some(status => status.hasUnmappedSchedules)
  );

  private readonly loadScheduleMappingStatuses = effect(() => {
    if (this.canViewOrganizations()) {
      void this.organizationManager.loadScheduleMappingStatuses();
    }
  });

  readonly navGroups = computed<DisplayNavGroup[]>(() => {
    const userRoles = this.authStore.roles();
    const isAwaitingFirstFacility = this.isAwaitingFirstFacility();
    const hasUnmappedOrganizationSchedules = this.hasUnmappedOrganizationSchedules();

    return this.allNavGroups
      .map(group => ({
        label: group.label,
        items: group.items
          .filter(item => !item.roles || item.roles.some(role => userRoles.includes(role)))
          .map(item => ({
            ...item,
            locked: isAwaitingFirstFacility && !item.worksWithoutFacility,
            hasWarning: item.route === '/organizations' && hasUnmappedOrganizationSchedules,
          })),
      }))
      .filter(group => group.items.length > 0);
  });
}
